import React, { useState, useEffect } from 'react';
import { masterSupabase } from '../../lib/supabase-master.js';
import { useAuth } from '../../contexts/AuthContext';

/**
 * SimpleOwnerSelector - Enhanced with user-based owner filtering
 * Only shows owners created by the current user (Multi-Tenant Support)
 */
const SimpleOwnerSelector = ({
  onOwnerChange,
  selectedOwner = 'all',
  disabled = false,
  className = ''
}) => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth(); // Get current user from AuthContext

  // Load owners on component mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadOwners();
    } else {
      // No user logged in, clear owners
      setOwners([]);
    }
  }, [user?.id]);

  const loadOwners = async () => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        console.log("🚫 No user ID available, cannot load owners");
        setOwners([]);
        return;
      }

      console.log("🔍 Loading owners for user:", user.id);
      
      // Filter owners by created_by = current user ID
      const { data: ownersData, error } = await masterSupabase
        .from('owners')
        .select('*')
        .eq('created_by', user.id) // ✅ FILTER BY CURRENT USER
        .order('name');
      
      if (error) {
        console.error('Error loading owners:', error);
        setOwners([]);
        return;
      }
      
      console.log(`✅ Loaded ${ownersData?.length || 0} owners for user ${user.id}`);
      setOwners(ownersData || []);
      
      // If no owners found and "all" is selected, notify parent
      if ((!ownersData || ownersData.length === 0) && selectedOwner === 'all') {
        console.log("📝 No owners found for user, switching to empty state");
        if (onOwnerChange) {
          onOwnerChange('none'); // Signal no owners available
        }
      }
      
    } catch (error) {
      console.error('Error loading owners:', error);
      setOwners([]);
    } finally {
      setLoading(false);
    }
  };

  const getOwnerDisplayName = (owner) => {
    if (owner.company_name && owner.company_name.trim()) {
      return owner.company_name;
    }
    return owner.name || 'Unbekannter Eigentümer';
  };

  const handleOwnerChange = (value) => {
    console.log("🔄 Owner selection changed to:", value);
    if (onOwnerChange) {
      onOwnerChange(value);
    }
  };

  // Show different states based on user and owners
  if (!user?.id) {
    return (
      <div className={`flex-shrink-0 ${className}`}>
        <div className="px-4 py-2 bg-gray-300 text-gray-600 border-0 shadow-lg rounded-xl min-w-48 text-center">
          Nicht angemeldet
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex-shrink-0 ${className}`}>
        <div className="px-4 py-2 bg-blue-100 text-blue-600 border-0 shadow-lg rounded-xl min-w-48 text-center flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          Lade Eigentümer...
        </div>
      </div>
    );
  }

  if (owners.length === 0) {
    return (
      <div className={`flex-shrink-0 ${className}`}>
        <div className="px-4 py-2 bg-yellow-100 text-yellow-800 border-0 shadow-lg rounded-xl min-w-48 text-center">
          Keine Eigentümer vorhanden
        </div>
        <div className="mt-1 text-xs text-yellow-600">
          Erstellen Sie zuerst einen Eigentümer
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-shrink-0 ${className}`}>
      <select
        value={selectedOwner}
        onChange={(e) => handleOwnerChange(e.target.value)}
        disabled={disabled}
        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg hover:shadow-xl focus:shadow-xl transition-all duration-300 font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 min-w-48"
      >
        <option value="all">Alle meine Eigentümer</option>
        {owners.map((owner) => (
          <option key={owner.id} value={owner.id} className="bg-white text-gray-900">
            {getOwnerDisplayName(owner)}
          </option>
        ))}
      </select>
      
      <div className="mt-1 text-xs text-blue-600">
        {owners.length} Eigentümer verfügbar
      </div>
    </div>
  );
};

export default SimpleOwnerSelector;
